import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft } from 'lucide-react';
import { Button } from './ui';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/**
 * Vista "Acceso denegado" con animacion de ratero entrando a hurtadillas.
 * Ocupa el 100% del ancho del contenedor. El SVG muestra un ladron con
 * capucha, antifaz y bolsa, caminando en puntillas — piernas alternando
 * cada 0.6s para simular pasos silenciosos.
 */
const AccesoDenegado = ({ mensaje }) => {
    const navigate = useNavigate();
    useDocumentTitle('Acceso denegado — Don Presupuesto');
    const textoDefault = 'No tienes permiso para ver esta página, solicita apoyo de administración o sigue navegando en otros lugares.';

    return (
        <div className="acceso-denegado-wrap">
            {/* SVG monitor con codigo saliendo — sin fondo ni base */}
            <div className="acceso-denegado-svg-wrap" aria-hidden="true">
                <svg
                    className="acceso-denegado-svg"
                    viewBox="0 0 400 260"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    {/* ===== Codigo/simbolos saliendo hacia arriba del monitor ===== */}
                    <g fontFamily="monospace" fontWeight="bold">
                        <text x="70" y="130" fill="rgb(var(--primary))" fontSize="18" opacity="0.9">
                            {'{ }'}
                            <animate attributeName="y" values="150;20" dur="3.5s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0;0.9;0" keyTimes="0;0.3;1" dur="3.5s" repeatCount="indefinite" />
                        </text>
                        <text x="115" y="150" fill="rgb(var(--cyan2, 34 211 238))" fontSize="14" opacity="0.85">
                            0x1F
                            <animate attributeName="y" values="150;10" dur="4s" begin="0.4s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0;0.85;0" keyTimes="0;0.3;1" dur="4s" begin="0.4s" repeatCount="indefinite" />
                        </text>
                        <text x="200" y="140" fill="rgb(var(--warning, 245 158 11))" fontSize="16" opacity="0.9">
                            {'</>'}
                            <animate attributeName="y" values="150;20" dur="3.8s" begin="0.8s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0;0.9;0" keyTimes="0;0.3;1" dur="3.8s" begin="0.8s" repeatCount="indefinite" />
                        </text>
                        <text x="270" y="150" fill="rgb(var(--primary))" fontSize="14" opacity="0.85">
                            null
                            <animate attributeName="y" values="150;10" dur="4.2s" begin="1.2s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0;0.85;0" keyTimes="0;0.3;1" dur="4.2s" begin="1.2s" repeatCount="indefinite" />
                        </text>
                        <text x="320" y="140" fill="rgb(var(--cyan2, 34 211 238))" fontSize="16" opacity="0.9">
                            ();
                            <animate attributeName="y" values="150;10" dur="3.6s" begin="1.6s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0;0.9;0" keyTimes="0;0.3;1" dur="3.6s" begin="1.6s" repeatCount="indefinite" />
                        </text>
                        <text x="150" y="160" fill="rgb(var(--danger, 239 68 68))" fontSize="12" opacity="0.9">
                            401
                            <animate attributeName="y" values="150;30" dur="4.5s" begin="2s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0;0.9;0" keyTimes="0;0.3;1" dur="4.5s" begin="2s" repeatCount="indefinite" />
                        </text>
                        <text x="240" y="160" fill="rgb(var(--primary))" fontSize="12" opacity="0.85">
                            ==
                            <animate attributeName="y" values="150;30" dur="4s" begin="2.4s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0;0.85;0" keyTimes="0;0.3;1" dur="4s" begin="2.4s" repeatCount="indefinite" />
                        </text>
                    </g>

                    {/* ===== Monitor (solo pantalla, sin base) ===== */}
                    <g className="acceso-laptop">
                        {/* Marco del monitor */}
                        <rect
                            x="90"
                            y="150"
                            width="220"
                            height="100"
                            rx="10"
                            fill="#1e293b"
                            stroke="#0f172a"
                            strokeWidth="5"
                        />
                        {/* Bisel superior con camara */}
                        <rect x="90" y="150" width="220" height="12" rx="5" fill="#0f172a" />
                        <circle cx="200" cy="156" r="2" fill="#334155" />

                        {/* Pantalla interior */}
                        <rect x="100" y="167" width="200" height="76" rx="3" fill="#0f172a" />

                        {/* Contenido de la pantalla — codigo animado + ACCESS DENIED */}
                        <text x="110" y="184" fill="rgb(var(--cyan2, 34 211 238))" fontFamily="monospace" fontSize="8" opacity="0.9">
                            {'> sudo access --request'}
                            <animate attributeName="opacity" values="0.9;0.3;0.9" dur="1.8s" repeatCount="indefinite" />
                        </text>
                        <text x="110" y="198" fill="rgb(var(--warning, 245 158 11))" fontFamily="monospace" fontSize="8" opacity="0.85">
                            [WARN] auth token invalid
                            <animate attributeName="opacity" values="0.85;0.3;0.85" dur="2s" begin="0.3s" repeatCount="indefinite" />
                        </text>
                        <text x="110" y="214" fill="rgb(var(--danger, 239 68 68))" fontFamily="monospace" fontSize="9" fontWeight="bold">
                            [FAIL] ACCESS DENIED
                            <animate attributeName="opacity" values="1;0.35;1" dur="0.9s" repeatCount="indefinite" />
                        </text>
                        {/* Cursor parpadeando */}
                        <text x="110" y="230" fill="rgb(var(--cyan2, 34 211 238))" fontFamily="monospace" fontSize="8">
                            {'> _'}
                            <animate attributeName="opacity" values="1;0.2;1" dur="0.6s" repeatCount="indefinite" />
                        </text>

                        {/* Candado rojo pequeno en la esquina superior derecha */}
                        <g transform="translate(285, 184)" className="acceso-lock-mini">
                            <circle r="10" fill="rgb(var(--danger, 239 68 68))" opacity="0.2" />
                            <circle r="8" fill="rgb(var(--danger, 239 68 68))" />
                            <path d="M -3 -1.5 L -3 -4 Q -3 -6.5 0 -6.5 Q 3 -6.5 3 -4 L 3 -1.5" stroke="#fff" strokeWidth="1" fill="none" />
                            <rect x="-4" y="-1.5" width="8" height="6" rx="1" fill="#fff" />
                        </g>
                    </g>
                </svg>
            </div>

            <h1 className="acceso-denegado-title">¡Quisiste sobrepasar la seguridad!</h1>
            <p className="acceso-denegado-subtitle">
                {mensaje || textoDefault}
            </p>

            <div className="acceso-denegado-actions">
                <Button
                    variant="primary"
                    size="lg"
                    leftIcon={<Home size={16} aria-hidden="true" />}
                    onClick={() => navigate('/')}
                >
                    Volver al inicio
                </Button>
                <button
                    type="button"
                    className="acceso-denegado-back"
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft size={14} aria-hidden="true" />
                    Regresar a la página anterior
                </button>
            </div>
        </div>
    );
};

export default AccesoDenegado;
